import { z } from 'zod';

const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Color must be a 6-digit hex like #62A2C3');

const assetUri = z.string().min(1, 'Asset URL or blob path is required');

export const brandKitLogoSchema = z.object({
  primarySvg: assetUri,
  primaryPng: assetUri,
  favicon: assetUri,
  emailHeader: assetUri,
  darkModePrimarySvg: assetUri.optional(),
  // Kate K13 — role-specific art. `primary*` is whatever shape the tenant
  // handed over (HTHD's is a padded square); these name the shape a surface
  // actually needs. All optional: a kit without them resolves every role to
  // the primary logo, exactly as before.
  horizontalSvg: assetUri.optional(),
  horizontalPng: assetUri.optional(),
  markSvg: assetUri.optional(),
  markPng: assetUri.optional(),
});

export const brandKitColorsSchema = z.object({
  primary: hexColor,
  secondary: hexColor,
  accent: hexColor,
  background: hexColor,
  text: hexColor,
  success: hexColor.optional(),
  warning: hexColor.optional(),
  danger: hexColor.optional(),
});

export const brandKitFontAssetSchema = z.object({
  family: z.string().min(1),
  weight: z.number().int(),
  style: z.enum(['normal', 'italic']),
  url: assetUri,
});

export const brandKitTypographySchema = z.object({
  primaryFontFamily: z.string().min(1),
  headingFontFamily: z.string().min(1),
  fontUrls: z.array(brandKitFontAssetSchema).default([]),
});

export const brandKitCopySchema = z.object({
  companyName: z.string().min(1),
  tagline: z.string().optional(),
  supportEmail: z.string().email(),
  supportPhone: z.string().optional(),
  legalEntityName: z.string().optional(),
  websiteUrl: z.string().url().optional(),
});

export const brandKitSchema = z.object({
  version: z.literal(1),
  logo: brandKitLogoSchema,
  colors: brandKitColorsSchema,
  typography: brandKitTypographySchema,
  copy: brandKitCopySchema,
  terminology: z.record(z.string(), z.string()).default({}),
});

export type BrandKitLogo = z.infer<typeof brandKitLogoSchema>;
export type BrandKitColors = z.infer<typeof brandKitColorsSchema>;
export type BrandKitFontAsset = z.infer<typeof brandKitFontAssetSchema>;
export type BrandKitTypography = z.infer<typeof brandKitTypographySchema>;
export type BrandKitCopy = z.infer<typeof brandKitCopySchema>;
export type BrandKit = z.infer<typeof brandKitSchema>;
